import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchApi } from "../../auth/api_two";
import Nav from "../../components/Home/Nav";
import Footer from "../../components/Home/Footer";
import {
  HiArrowRight,
  HiChevronDown,
  HiChevronUp,
  HiInformationCircle,
  HiCollection,
  HiSearch,
} from "react-icons/hi";
import { FaWhatsapp } from "react-icons/fa";
import CardImageGallery from "../../components/Home/CardImageGallery";

const AllServicesWithParent = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchServicesWithParent = async () => {
      try {
        const response = await fetchApi("get", "servicesWithNullParent");
        setServices(
          (response.data || []).map((s) => ({ ...s, expanded: false })),
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchServicesWithParent();
  }, []);

  const toggleExpandService = (id) => {
    setServices((prev) =>
      prev.map((s) => (s.id === id ? { ...s, expanded: !s.expanded } : s)),
    );
  };

  const filteredServices = services.filter((s) =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-slate-50 min-h-screen">
      <Nav />

      {/* Top padding to clear fixed navbar (top bar 40px + nav ~64px = ~110px total) */}
      <div className="pt-28 md:pt-32" />

      {/* Category Pills Panel */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
        <div className="bg-white rounded-[2rem] p-5 md:p-8 border border-slate-200/80 shadow-sm">
          {/* Panel Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-black uppercase tracking-widest mb-1.5">
                Explore Catalog
              </span>
              <h1 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">
                Service Categories
              </h1>
            </div>

            {/* Search within categories */}
            <div className="relative w-full sm:w-72">
              <HiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm font-semibold text-slate-800 bg-slate-50 border border-slate-200 focus:border-blue-400 focus:bg-white rounded-xl outline-none transition-all"
              />
            </div>
          </div>

          {/* Hint */}
          <p className="text-[11px] text-slate-400 font-medium mb-4">
            {loading
              ? "Loading categories..."
              : `${filteredServices.length} ${filteredServices.length === 1 ? "category" : "categories"} available — click any to explore its services`}
          </p>

          {/* Category Pills — wraps naturally, scrollable on tiny screens */}
          {loading ? (
            <div className="flex flex-wrap gap-3">
              {Array(6).fill(0).map((_, i) => (
                <div key={i} className="h-11 w-36 bg-slate-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm font-bold uppercase tracking-widest">
              No categories match "{searchTerm}"
            </div>
          ) : (
            <div className="flex flex-wrap gap-2.5 md:gap-3">
              {filteredServices.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/childServices/${cat.id}`}
                  className="group flex items-center gap-2.5 px-4 md:px-5 py-2.5 md:py-3 bg-slate-50 hover:bg-blue-600 text-slate-800 hover:text-white rounded-2xl border border-slate-200/80 hover:border-blue-600 shadow-sm hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 active:scale-95"
                >
                  <HiCollection
                    className="text-blue-500 group-hover:text-white transition-colors shrink-0"
                    size={16}
                  />
                  <span className="font-bold text-xs md:text-sm tracking-wide capitalize whitespace-nowrap">
                    {cat.name}
                  </span>
                  <HiArrowRight
                    className="text-slate-400 group-hover:text-white group-hover:translate-x-1 transition-all shrink-0"
                    size={14}
                  />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Services Grid */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-32">
        {/* Section header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
            All Service Categories
          </span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {loading ? (
            Array(6)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="h-[450px] bg-slate-200 animate-pulse rounded-[2.5rem]"
                ></div>
              ))
          ) : services.length === 0 ? (
            <div className="col-span-full py-20 text-center">
              <HiInformationCircle
                size={64}
                className="mx-auto text-slate-200 mb-4"
              />
              <p className="text-slate-400 font-bold uppercase tracking-widest">
                No services found in our catalog
              </p>
            </div>
          ) : (
            services.map((service) => (
              <div
                key={service.id}
                className="group bg-white rounded-[2.5rem] border border-slate-200/60 overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-blue-200/40 transition-all duration-500 flex flex-col hover:-translate-y-2"
              >
                <div className="relative">
                  <CardImageGallery imageSrc={service.service_image} altTitle={service.name} className="h-64 md:h-72" />
                  <div className="absolute top-4 left-4 z-10">
                    <span className="px-4 py-2 glass border border-white/40 text-[10px] font-black text-slate-900 uppercase tracking-widest rounded-xl shadow-lg backdrop-blur-md">
                      Premium Service
                    </span>
                  </div>
                </div>

                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="text-2xl font-black text-slate-900 mb-4 capitalize group-hover:text-blue-600 transition-colors">
                    {service.name}
                  </h3>

                  <div
                    className={`text-slate-500 text-sm leading-relaxed mb-8 transition-all duration-500 ${service.expanded ? "max-h-[1000px] opacity-100" : "max-h-12 opacity-80 overflow-hidden"}`}
                  >
                    <p>{service.description}</p>
                  </div>

                  <div className="mt-auto flex flex-col gap-4">
                    <div className="flex items-center justify-between border-t border-slate-100 pt-6">
                      <button
                        onClick={() => toggleExpandService(service.id)}
                        className="text-slate-400 hover:text-blue-600 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest transition-colors"
                      >
                        {service.expanded ? (
                          <>Collapse <HiChevronUp /></>
                        ) : (
                          <>Details <HiChevronDown /></>
                        )}
                      </button>

                      <div className="flex items-center gap-3">
                        <Link
                          to={`/childServices/${service.id}`}
                          className="px-6 py-3 bg-blue-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-900 transition-all shadow-lg shadow-blue-100 active:scale-95 flex items-center gap-2"
                        >
                          View Services <HiArrowRight />
                        </Link>

                        <a
                          href={`https://wa.me/+9779801037595?text=${encodeURIComponent(`Hello, I would like to inquire about the service: ${service.name}. Link: ${window.location.origin}/childServices/${service.id}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-3 bg-emerald-500 text-white rounded-2xl hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-100 hover:shadow-emerald-200 active:scale-95 flex items-center"
                          title="Query on WhatsApp"
                        >
                          <FaWhatsapp size={20} />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AllServicesWithParent;
